import React, { useState } from 'react';
import { X, Code, ExternalLink, MessageCircle, Info } from 'lucide-react';
import type { BodyEndEditModalProps, TabType } from './types';
import { TABS } from './types';
import { useBodyEndForm } from './useBodyEndForm';
import { InlineScriptsTab } from './InlineScriptsTab';
import { ExternalScriptsTab } from './ExternalScriptsTab';
import { ChatWidgetsTab } from './ChatWidgetsTab';
import { NoScriptTab } from './NoScriptTab';

const TabIcon: React.FC<{ icon: string; size?: number }> = ({ icon, size = 16 }) => {
  switch (icon) {
    case 'code':
      return <Code size={size} />;
    case 'external':
      return <ExternalLink size={size} />;
    case 'chat':
      return <MessageCircle size={size} />;
    case 'info':
      return <Info size={size} />;
    default:
      return null;
  }
};

export const BodyEndEditModal: React.FC<BodyEndEditModalProps> = ({
  isOpen,
  onClose,
  onSave,
  initialData = {},
  isLoading = false,
}) => {
  const [activeTab, setActiveTab] = useState<TabType>('inline');
  const {
    formData,
    handleInputChange,
    handleAddScriptLink,
    handleScriptLinkChange,
    handleRemoveScriptLink,
    handleAddChatWidget,
    handleChatWidgetChange,
    handleRemoveChatWidget,
  } = useBodyEndForm(isOpen, initialData);

  const handleSave = () => {
    console.log('💾 Saving Scripts & Widgets data:', formData);
    onSave(formData);
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-[70]">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-4xl max-h-[90vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <div className="flex items-center space-x-3">
            <Code className="text-blue-600" size={24} />
            <h2 className="text-xl font-semibold text-gray-900">Scripts & Widgets</h2>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 transition-colors">
            <X size={24} />
          </button>
        </div>

        {/* Tabs */}
        <div className="border-b border-gray-200">
          <div className="flex space-x-1 px-6">
            {TABS.map(tab => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`px-4 py-3 text-sm font-medium border-b-2 transition-colors ${
                  activeTab === tab.id
                    ? 'border-blue-600 text-blue-600'
                    : 'border-transparent text-gray-600 hover:text-gray-900'
                }`}
              >
                <div className="flex items-center space-x-2">
                  <TabIcon icon={tab.icon} />
                  <span>{tab.label}</span>
                </div>
              </button>
            ))}
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6">
          {activeTab === 'inline' && (
            <InlineScriptsTab formData={formData} onInputChange={handleInputChange} />
          )}
          {activeTab === 'external' && (
            <ExternalScriptsTab
              scriptLinks={formData.scriptLinks}
              onAdd={handleAddScriptLink}
              onChange={handleScriptLinkChange}
              onRemove={handleRemoveScriptLink}
            />
          )}
          {activeTab === 'chat' && (
            <ChatWidgetsTab
              chatWidgets={formData.chatWidgets}
              onAdd={handleAddChatWidget}
              onChange={handleChatWidgetChange}
              onRemove={handleRemoveChatWidget}
            />
          )}
          {activeTab === 'noscript' && (
            <NoScriptTab formData={formData} onInputChange={handleInputChange} />
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end space-x-3 p-6 border-t border-gray-200">
          <button
            onClick={onClose}
            className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-md transition-colors"
            disabled={isLoading}
          >
            Cancel
          </button>
          <button
            onClick={handleSave}
            disabled={isLoading}
            className="px-4 py-2 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-md transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isLoading ? 'Saving...' : 'Save Changes'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default BodyEndEditModal;
